import { Crop } from 'react-image-crop';

import { ImageEditSettings, ImageMetadata } from './types';

export function getImageEditCacheKey(editValues?: ImageEditSettings) {
  if (!editValues) return '_initial';

  // Create a filename-safe key based on edit parameters
  let key = '';

  if (editValues.flip) {
    key += `_f${editValues.flip}`;
  }

  if (editValues.mirror) {
    key += '_m';
  }

  if (editValues.crop) {
    const { x, y, width, height } = editValues.crop;
    key += `_c${x}-${y}-${width}-${height}`;
  }

  if (key === '') {
    return '_initial';
  }

  return key;
}

export const readFileAsBase64 = async (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => {
      resolve(reader.result as string);
    };
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

export const getFileExtensions = (accept: Record<string, string[]>): string => {
  const extensions = Object.values(accept)
    .flat()
    .map((e) => e.replace('.', ''));
  if (!extensions.length) return '';

  // Remove dots and join with comma
  if (extensions.length === 1) {
    return extensions[0].replace('.', '');
  }
  return extensions.slice(0, -1).join(', ') + ' or ' + extensions.slice(-1);
};

export function percentageCropToAbsoluteCrop(
  percentageCrop?: Omit<Crop, 'unit'>,
  imageMetadata?: ImageMetadata,
): Crop | undefined {
  if (!percentageCrop || !imageMetadata) return undefined;

  return {
    x: (percentageCrop.x / 100) * imageMetadata.width,
    y: (percentageCrop.y / 100) * imageMetadata.height,
    width: (percentageCrop.width / 100) * imageMetadata.width,
    height: (percentageCrop.height / 100) * imageMetadata.height,
    unit: 'px',
  };
}

export function absoluteCropToPercentageCrop(
  absoluteCrop?: Omit<Crop, 'unit'>,
  imageMetadata?: ImageMetadata,
): Crop | undefined {
  if (!absoluteCrop || !imageMetadata) return undefined;

  return {
    x: (absoluteCrop.x / imageMetadata.width) * 100,
    y: (absoluteCrop.y / imageMetadata.height) * 100,
    width: (absoluteCrop.width / imageMetadata.width) * 100,
    height: (absoluteCrop.height / imageMetadata.height) * 100,
    unit: '%',
  };
}

/**
 * Rotates a crop area by 90 degrees clockwise
 * @param crop The crop area to rotate (in percentage coordinates)
 * @param isMirrored Whether the image is currently mirrored
 * @returns The rotated crop area
 */
export function rotateCropBy90Degrees(
  crop: Crop | undefined,
  isMirrored: boolean = false,
): Crop | undefined {
  if (!crop) return undefined;
  if (crop.unit !== '%') {
    console.error('rotateCropBy90Degrees - crop unit is not %');
    return crop;
  }

  let rotated: Crop;

  if (isMirrored) {
    // Different transformation when mirrored
    rotated = {
      x: crop.y,
      y: 100 - (crop.x + crop.width),
      width: crop.height,
      height: crop.width,
      unit: '%',
    };
  } else {
    // Original transformation when not mirrored
    rotated = {
      x: 100 - (crop.y + crop.height),
      y: crop.x,
      width: crop.height,
      height: crop.width,
      unit: '%',
    };
  }

  return rotated;
}

export function mirrorCrop(crop: Crop | undefined): Crop | undefined {
  if (!crop) return undefined;
  if (crop.unit !== '%') {
    console.error('mirrorCrop - crop unit is not %');
    return crop;
  }

  const mirrored: Crop = {
    x: 100 - crop.x - crop.width,
    y: crop.y,
    width: crop.width,
    height: crop.height,
    unit: '%',
  };
  return mirrored;
}

// Omit multiple keys from an object
export function omitKeys<T extends object, K extends keyof T>(
  obj: T,
  keys: readonly K[],
): Omit<T, K> {
  const result = { ...obj };
  keys.forEach((key) => delete result[key]);
  return result;
}

// Get metadata from an image file
export async function getImageMetadata(file: Blob): Promise<ImageMetadata> {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);

    img.onload = () => {
      URL.revokeObjectURL(url);
      resolve({
        width: img.naturalWidth,
        height: img.naturalHeight,
      });
    };

    img.onerror = () => {
      URL.revokeObjectURL(url);
      reject(new Error('Failed to load image'));
    };

    img.src = url;
  });
}
